import React, { useCallback, useEffect, useState } from "react";
import { ShieldOff, HelpCircle, Activity, CheckCircle2 } from "lucide-react";
import Card from "./Card";
import { useRBAC } from "../hooks/useRBAC";
import AiPendingApprovalsBanner from "./AiPendingApprovalsBanner";
import AiUnclaimedEndpoints from "./AiUnclaimedEndpoints";
import AiEstateChanges from "./AiEstateChanges";
import { ACTION_CENTER_CATEGORIES, actionCenterSummary } from "../utils/aiActionCenter";

/**
 * One attention surface instead of three stacked cards. Approvals, unclaimed endpoints, and
 * estate changes were separately-styled widgets all meaning "someone should look at this",
 * competing for the same glance. Here they share a card with category tabs and count badges,
 * ordered by the rule §9.1 established: decisions first, news second — approvals and ownership
 * questions need someone to act; estate changes need someone to know.
 *
 * The children keep their own fetching, permissions, and empty behavior; every panel stays
 * mounted (hidden, not unmounted) so counts load up front and badges never lag the panels.
 * Category order and count arithmetic live in utils/aiActionCenter (tested there).
 */
const PRESENTATION = {
  approvals: { label: "Approvals", Icon: ShieldOff },
  unclaimed: { label: "Needs an owner", Icon: HelpCircle },
  changes: { label: "What changed", Icon: Activity },
};
const CATEGORIES = ACTION_CENTER_CATEGORIES.map((c) => ({ ...c, ...PRESENTATION[c.key] }));

export default function AiActionCenter({
  canManage, onOpenAsset, onReviewed, refreshKey, lastScannedAt, onSummaryChange,
  changeFilter = null, onClearChangeFilter,
}) {
  const { canViewAllAiEstateData } = useRBAC();
  const org = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const canViewEstate = canViewAllAiEstateData(org?.organizationId);
  const [counts, setCounts] = useState({ approvals: null, unclaimed: null, changes: null });
  // True when the approvals request failed or was withheld: the card must not then say there is
  // nothing waiting, because that is exactly what it does not know.
  const [unavailable, setUnavailable] = useState({ approvals: false, unclaimed: false, changes: false });
  const unavailableFor = useCallback(
    (key) => (v) => setUnavailable((u) => (u[key] === !!v ? u : { ...u, [key]: !!v })),
    []
  );
  // null until the user picks — the effective tab follows the counts (first category with
  // decisions waiting) so the card opens on whatever actually needs someone.
  const [chosen, setChosen] = useState(null);
  // A figure in the estate change panel opened the list behind it: land on that list.
  useEffect(() => {
    if (changeFilter) setChosen("changes");
  }, [changeFilter]);

  // True when the viewer cannot decide approvals: the queue was never read, so it is neither
  // empty nor failed.
  const [approvalsNA, setApprovalsNA] = useState(false);
  const countFor = useCallback(
    (key) => (n) => setCounts((c) => (c[key] === n ? c : { ...c, [key]: n })),
    []
  );

  const { firstWithWork, total, decisionsWaiting } = actionCenterSummary(counts);
  const active = chosen ?? firstWithWork;

  // The dashboard's tab badge — the card may live behind a non-default tab, and moving work off
  // the landing view is only acceptable if the count stays impossible to miss.
  useEffect(() => {
    onSummaryChange?.({ total, decisionsWaiting });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, decisionsWaiting]);

  // Before the first scan there is genuinely nothing to report on — same rule the estate-change
  // card follows on its own.
  if (!lastScannedAt) return null;

  // Every child here fetches an org-wide list on mount, so a caller without the estate
  // capability gets none of them mounted rather than three requests that 403.
  if (!canViewEstate) return null;

  const anyUnavailable = Object.values(unavailable).some(Boolean);
  // Every panel actually read returned a valid result: only then is zero a statement of fact.
  // Approvals not applicable to this viewer was not read, so it is left out of that claim.
  const allKnown = !anyUnavailable
    && Object.entries(counts).every(([k, n]) => (k === "approvals" && approvalsNA) || n !== null);
  const known = (key) => counts[key] !== null && !unavailable[key];
  const panelClass = (key) => (active === key ? "" : "hidden");

  return (
    <Card className={`p-4 mb-4 ${decisionsWaiting > 0 ? "border-l-4 border-l-amber-400" : ""}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-semibold text-content-primary">Needs attention</span>
        {total > 0 ? (
          <>
            <span className="px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800 border border-amber-300">
              {total}
            </span>
            {anyUnavailable && (
              <span className="text-[11px] text-content-secondary">Some panels are unavailable</span>
            )}
          </>
        ) : allKnown ? (
          <span className="flex items-center gap-1 text-[11px] text-content-secondary">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            {approvalsNA ? "Nothing waiting in the panels you can see" : "Nothing waiting"}
          </span>
        ) : (
          <span className="text-[11px] text-content-secondary">
            {anyUnavailable ? "Some panels are unavailable" : "Checking…"}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {CATEGORIES.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setChosen(key)}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border ${
                active === key
                  ? "border-border bg-theme-hover text-content-primary font-medium"
                  : "border-transparent text-content-secondary hover:bg-theme-hover"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
              {(counts[key] ?? 0) > 0 && (
                <span className="px-1 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800">
                  {counts[key]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* All panels stay mounted so each child fetches and reports its count immediately. */}
      <div className={panelClass("approvals")}>
        <AiPendingApprovalsBanner
          onOpenAsset={onOpenAsset}
          refreshKey={refreshKey}
          onCountChange={countFor("approvals")}
          onUnavailable={unavailableFor("approvals")}
          onNotApplicable={setApprovalsNA}
          embedded
        />
        {!approvalsNA && known("approvals") && counts.approvals === 0 && (
          <p className="text-sm text-content-secondary">No response actions awaiting approval.</p>
        )}
      </div>
      <div className={panelClass("unclaimed")}>
        <AiUnclaimedEndpoints
          canManage={canManage}
          onReviewed={onReviewed}
          refreshKey={refreshKey}
          onCountChange={countFor("unclaimed")}
          onUnavailable={unavailableFor("unclaimed")}
          embedded
        />
        {known("unclaimed") && counts.unclaimed === 0 && (
          <p className="text-sm text-content-secondary">
            No discovered endpoints are waiting for an ownership answer.
          </p>
        )}
      </div>
      <div className={panelClass("changes")}>
        <AiEstateChanges
          canManage={canManage}
          onOpenAsset={onOpenAsset}
          refreshKey={refreshKey}
          lastScannedAt={lastScannedAt}
          onCountChange={countFor("changes")}
          onUnavailable={unavailableFor("changes")}
          typeFilter={changeFilter}
          onClearTypeFilter={onClearChangeFilter}
          embedded
        />
      </div>
    </Card>
  );
}
